import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Lock, Save, ShieldCheck, User as UserIcon, Loader2, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { profileApi, apiError, getUser, saveSession, mediaUrl } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOCKED_FIELDS = [
  ["nombre", "Nombre"],
  ["apellidos", "Apellidos"],
  ["ciudad", "Ciudad"],
  ["pais", "País"],
];
const EDITABLE_FIELDS = [
  ["whatsapp", "WhatsApp"],
  ["edad", "Edad"],
  ["colegio", "Colegio / Institución"],
];
const FIELDS = [...LOCKED_FIELDS, ...EDITABLE_FIELDS];

export default function UserProfile() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [user, setUser] = useState(getUser());
  const [form, setForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    const u = getUser() || {};
    setForm(FIELDS.reduce((acc, [k]) => ({ ...acc, [k]: u[k] || "" }), {}));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const persist = (u) => { setUser(u); saveSession({ user: u }); };

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      // Solo se envían los campos editables; los bloqueados no se modifican.
      const payload = EDITABLE_FIELDS.reduce((acc, [k]) => ({ ...acc, [k]: form[k] || "" }), {});
      const updated = await profileApi.update(payload);
      persist(updated);
      toast.success("Perfil actualizado.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingInfo(false); }
  };

  const notifyLocked = () => toast.info("Por seguridad, este dato solo lo puede cambiar el super administrador. Solicítalo por el Chat Privado.");

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5 MB."); return; }
    setUploading(true);
    try {
      const { avatar_url } = await profileApi.uploadAvatar(file);
      persist({ ...(getUser() || {}), avatar_url });
      toast.success("Foto actualizada.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setSavingPw(true);
    try {
      await profileApi.changePassword(pw.current_password, pw.new_password);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Contraseña actualizada.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPw(false); }
  };

  const avatar = mediaUrl(user?.avatar_url);
  const initials = `${(user?.nombre || "L")[0] || ""}${(user?.apellidos || "")[0] || ""}`.toUpperCase();

  return (
    <div className="min-h-screen bg-cream" data-testid="user-profile-page">
      <header className="bg-navy">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="profile-back-btn" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Mi cuenta
          </button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center">
            <UserIcon className="w-7 h-7 text-[#0A1C30]" />
          </span>
          <div>
            <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">TU CUENTA</p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A1C30]">Mi perfil</h1>
          </div>
        </div>

        {/* Avatar */}
        <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6 flex items-center gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-navy flex items-center justify-center ring-4 ring-[var(--gold)]/30">
              {avatar ? <img src={avatar} alt="Avatar" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                : <span className="text-3xl font-extrabold text-gold">{initials}</span>}
            </div>
            <button data-testid="profile-avatar-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[var(--gold)] text-[#0A1C30] flex items-center justify-center shadow-md hover:scale-105 transition-transform disabled:opacity-60">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickAvatar} data-testid="profile-avatar-input" />
          </div>
          <div>
            <p className="font-extrabold text-[#0A1C30] text-lg">{[user?.nombre, user?.apellidos].filter(Boolean).join(" ") || "Líder Supera"}</p>
            <p className="text-sm text-[#6B7688]">{user?.email}</p>
            <p className="text-xs text-[#6B7688] mt-1">JPG, PNG o WEBP · máx 5 MB</p>
          </div>
        </section>

        {/* Personal info */}
        <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6">
          <h2 className="font-extrabold text-[#0A1C30] mb-4">Datos personales</h2>

          {/* Aviso de datos bloqueados */}
          <div className="mb-5 rounded-2xl bg-[#FBF3D9] ring-1 ring-[#E9D9A0] p-4 flex items-start gap-3" data-testid="profile-locked-notice">
            <Lock className="w-5 h-5 text-[#B99B2E] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#0A1C30]">Nombre, apellidos, ciudad y país no se pueden editar</p>
              <p className="text-sm text-[#6B7688] mt-0.5">Por seguridad, si necesitas corregir alguno de estos datos, solicítalo al super administrador por el Chat Privado.</p>
              <button data-testid="profile-goto-chat" onClick={() => navigate("/chat")} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-4 py-2 text-sm hover:bg-[#0d2440] transition-colors">
                <MessagesSquare className="w-4 h-4" /> Solicitar cambio por Chat Privado
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {LOCKED_FIELDS.map(([k, label]) => (
              <div key={k} onClick={notifyLocked} className="cursor-pointer">
                <Label className="mb-1 flex items-center gap-1 font-bold text-xs">
                  <Lock className="w-3.5 h-3.5 text-[#B99B2E]" /> {label}
                </Label>
                <div className="pointer-events-none">
                  <Input data-testid={`profile-${k}`} value={form[k] || ""} disabled className="bg-[#F4F6F9] text-[#6B7688]" />
                </div>
              </div>
            ))}
            {EDITABLE_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="mb-1 block font-bold text-xs">{label}</Label>
                <Input data-testid={`profile-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
            <div>
              <Label className="mb-1 block font-bold text-xs">Programa</Label>
              <Input value={user?.programa || ""} disabled className="bg-[#F4F6F9] text-[#6B7688]" />
            </div>
            <div>
              <Label className="mb-1 block font-bold text-xs">Grupo</Label>
              <Input data-testid="profile-grupo" value={user?.grupo ? `Grupo ${user.grupo}` : "Sin asignar"} disabled className="bg-[#F4F6F9] text-[#6B7688]" />
            </div>
            <div>
              <Label className="mb-1 flex items-center gap-1 font-bold text-xs">
                <ShieldCheck className="w-3.5 h-3.5 text-[#22A860]" /> Referencia Supera
              </Label>
              <Input data-testid="profile-referencia" value={user?.referencia || "—"} disabled className="bg-[#F4F6F9] text-[#6B7688]" />
              <p className="text-[11px] text-[#6B7688] mt-1">Este dato no se puede editar.</p>
            </div>
          </div>
          <button data-testid="profile-save-info" onClick={saveInfo} disabled={savingInfo}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 disabled:opacity-60">
            {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios
          </button>
        </section>

        {/* Password */}
        <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6">
          <h2 className="flex items-center gap-2 font-extrabold text-[#0A1C30] mb-4"><Lock className="w-5 h-5 text-[#E9A400]" /> Cambiar contraseña</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="mb-1 block font-bold text-xs">Contraseña actual</Label>
              <Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block font-bold text-xs">Nueva contraseña</Label>
              <Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block font-bold text-xs">Confirmar</Label>
              <Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} />
            </div>
          </div>
          <button data-testid="pw-save" onClick={changePassword} disabled={savingPw}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-navy text-white font-extrabold px-6 py-2.5 disabled:opacity-60">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Actualizar contraseña
          </button>
        </section>
      </div>
    </div>
  );
}
